import React, { useEffect } from 'react'
import cx from 'classnames'
import { isMobile, isIPad13 } from 'react-device-detect'
import useDimensions from 'react-use-dimensions'

import Banner from '@cinch/oxd-components/banner/src'
import { Shield } from '@cinch/oxd-components/icons/src'
import VehiclesFilter from '../vehicles-filter/src'

import useViewport from '@cinch/oxd-hooks/viewport/src'
import { supportsObjectFit } from '@cinch/oxd-utils/browser-support/src'
import tokens from '@cinch/oxd-utils/tokens/src'

import * as styles from './home-hero.module.css'

export interface HeroProps {
  i18n: Cinch.WebAppTranslations['home']
  heroImage: Cinch.Picture
}

const HomeHero: React.FC<HeroProps> = ({ i18n, heroImage }) => {
  const [bannerRef, { height: bannerHeight = 0 }] = useDimensions()
  const { vh } = useViewport({ updateOnResize: false, updateOnOrientationChange: true })

  const [objectFitSupported, setObjectFitSupported] = React.useState<boolean | null>(null)
  useEffect(() => {
    setObjectFitSupported(supportsObjectFit())
  }, [])

  return (
    <div className={styles.hero}>
      <div className={styles.heroWrapper}>
        <div
          className={styles.heroInner}
          style={isMobile || isIPad13 ? { minHeight: `${vh * 100 - bannerHeight}px` } : {}}
        >
          {heroImage && (
            <div
              className={cx(styles.heroImageContainer, { [styles.backgroundFit]: objectFitSupported === false })}
              style={objectFitSupported === false && heroImage.l ? { backgroundImage: `url(${heroImage.l.png})` } : {}}
            >
              {objectFitSupported && (
                <picture>
                  {heroImage.s && (
                    <>
                      <source srcSet={heroImage.s.webp} media={tokens.customMedia.xxs} type="image/webp" />
                      <source srcSet={heroImage.s.png} media={tokens.customMedia.xxs} type="image/png" />
                    </>
                  )}
                  {heroImage.l && (
                    <>
                      <source srcSet={heroImage.l.webp} media={tokens.customMedia.s} type="image/webp" />
                      <source srcSet={heroImage.l.png} media={tokens.customMedia.s} type="image/png" />
                      <img className={styles.heroImage} alt={i18n.heroImageAlt} src={heroImage.l.png} />
                    </>
                  )}
                </picture>
              )}
            </div>
          )}
          <h1 className={styles.heading}>
            <span dangerouslySetInnerHTML={{ __html: 'Cars delivered to your door' }} />
          </h1>
          <div className={styles.heroFooter}>
            <p className={cx(styles.headingBody, styles.commitment)}>{i18n.body}</p>
            <VehiclesFilter heading={i18n.filterHeading} />
          </div>
        </div>
      </div>
      <div ref={bannerRef}>
        <Banner
          title={i18n.bannerTitle}
          subTitle={i18n.bannerSubTitle}
          icon={Shield}
          rightArrow={true}
          href="/information"
          tag={{
            event: 'Click',
            eventCategory: 'Banner',
            eventLabel: 'Information',
            eventAction: i18n.bannerTitle,
          }}
        />
      </div>
    </div>
  )
}

export default HomeHero
